import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Panel, PageHeader, StyledSelect, TextField, Label, PrimaryButton, GhostButton, Chip } from "@/components/kit";
import { PackagePlus, Plus, Trash2, Loader2 } from "lucide-react";

const emptyLine = () => ({ item_id: "", bin_id: "", quantity: "", unit_cost: "" });

export default function GoodsReceipt() {
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [bins, setBins] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [form, setForm] = useState({ supplier_id: "", warehouse_id: "", notes: "" });
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  const loadReceipts = () => api.get("/goods-receipts").then((r) => setReceipts(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/refs/suppliers"), api.get("/refs/items"), api.get("/refs/warehouses"),
    ]).then(([s, i, w]) => {
      setSuppliers(s.data); setItems(i.data); setWarehouses(w.data);
      if (w.data[0]) setForm((f) => ({ ...f, warehouse_id: w.data[0].id }));
      if (s.data[0]) setForm((f) => ({ ...f, supplier_id: s.data[0].id }));
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    loadReceipts();
  }, []);

  useEffect(() => {
    if (!form.warehouse_id) return;
    api.get("/refs/bins").then((r) => setBins((r.data || []).filter((b) => b.warehouse_id === form.warehouse_id))).catch(() => setBins([]));
  }, [form.warehouse_id]);

  const setLine = (idx, key, val) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [key]: val } : l)));
  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (idx) => setLines((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));

  const submit = async (e) => {
    e.preventDefault();
    const payloadLines = lines
      .filter((l) => l.item_id && l.bin_id && Number(l.quantity) > 0)
      .map((l) => ({ item_id: l.item_id, bin_id: l.bin_id, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost) || 0 }));
    if (payloadLines.length === 0) { toast.error("Add at least one valid line (item, bin, quantity)"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/goods-receipts", { ...form, lines: payloadLines });
      toast.success(`${data.receipt_number} posted · ${data.posted_transactions.length} ledger entries`);
      setLines([emptyLine()]);
      setForm((f) => ({ ...f, notes: "" }));
      await loadReceipts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="goods-receipt-page">
      <PageHeader title="Goods Receipt" subtitle="Receive stock against a supplier — each line posts a real, immutable entry into the inventory ledger." />

      <Panel className="p-5">
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>Supplier</Label><div className="mt-1.5">
              <StyledSelect value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} data-testid="gr-supplier">
                <option value="">— none —</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </StyledSelect></div></div>
            <div><Label>Receiving warehouse</Label><div className="mt-1.5">
              <StyledSelect value={form.warehouse_id} onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} required data-testid="gr-warehouse">
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </StyledSelect></div></div>
            <div><Label>Reference / notes</Label><div className="mt-1.5">
              <TextField value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. PO-00042" data-testid="gr-notes" /></div></div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Receipt lines</Label>
              <GhostButton type="button" onClick={addLine} data-testid="gr-add-line"><Plus className="h-4 w-4" /> Add line</GhostButton>
            </div>
            <div className="space-y-2" data-testid="gr-lines">
              {lines.map((l, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_120px_40px] gap-2 items-center">
                  <StyledSelect value={l.item_id} onChange={(e) => setLine(idx, "item_id", e.target.value)} data-testid={`gr-line-item-${idx}`}>
                    <option value="">Select item…</option>
                    {items.map((it) => <option key={it.id} value={it.id}>{`${it.sku} · ${it.name}`}</option>)}
                  </StyledSelect>
                  <StyledSelect value={l.bin_id} onChange={(e) => setLine(idx, "bin_id", e.target.value)} data-testid={`gr-line-bin-${idx}`}>
                    <option value="">Select bin…</option>
                    {bins.map((b) => <option key={b.id} value={b.id}>{b.code}</option>)}
                  </StyledSelect>
                  <TextField type="number" min="0" step="any" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(idx, "quantity", e.target.value)} data-testid={`gr-line-qty-${idx}`} />
                  <TextField type="number" min="0" step="any" placeholder="Unit cost" value={l.unit_cost} onChange={(e) => setLine(idx, "unit_cost", e.target.value)} data-testid={`gr-line-cost-${idx}`} />
                  <button type="button" onClick={() => removeLine(idx)} className="text-slate-500 hover:text-rose-400 flex justify-center" data-testid={`gr-line-remove-${idx}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <PrimaryButton type="submit" disabled={saving} data-testid="gr-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackagePlus className="h-4 w-4" />} Post Goods Receipt
            </PrimaryButton>
          </div>
        </form>
      </Panel>

      <div>
        <div className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-3">Recent receipts</div>
        <Panel className="overflow-hidden">
          <table className="w-full text-sm" data-testid="receipts-table">
            <thead><tr className="text-left font-mono text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
              <th className="px-4 py-3">Receipt #</th><th className="px-4 py-3">Supplier</th><th className="px-4 py-3">Warehouse</th>
              <th className="px-4 py-3 text-right">Lines</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th>
            </tr></thead>
            <tbody>
              {receipts.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500 text-sm">No receipts yet.</td></tr>}
              {receipts.map((r) => (
                <tr key={r.id} className="border-b border-slate-800/60" data-testid={`receipt-row-${r.receipt_number}`}>
                  <td className="px-4 py-3 font-mono text-orange-400">{r.receipt_number}</td>
                  <td className="px-4 py-3 text-slate-300">{r.supplier_name}</td>
                  <td className="px-4 py-3 text-slate-300">{r.warehouse_name}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-400">{r.line_count}</td>
                  <td className="px-4 py-3"><Chip tone="emerald">{r.status}</Chip></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{r.received_date ? new Date(r.received_date).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
    </div>
  );
}
